import DataExchangeService from '../../../services/data-exchange.service';
import { Consent } from '../../../dataTypes/type-consent';
import { ConsentsStatusService } from '../../../services/consents-services/consents-status.service';
import { debounce } from 'ts-debounce';
import { DataService } from '../../../services/utilities/data.service';
import { GetConsentsService } from '../../../services/consents-services/get-consents.service';
import { SettingsService } from '../../../services/settings-service';
import { UtilityService } from '../../../services/utility.service';

const _ = require('lodash');
const categoryInfoContentClassName: string = 'uc-info-modal-category-content';
const closeConsentInfoModalEventName: string = 'close-consents-info-modal';
const consentInfoContentClassName: string = 'uc-consent-info-content';
const consentsInfoForSearchingContentId: string = 'uc-consents-info-search-content';
const consentInfoModalTemplate: string = require('./templates/consents-info-modal-template-1.hbs');
const consentsInfoModalId: string = 'uc-consents-info-modal';
const parentId: string = 'uc-consents-info-modal';
const searchFieldId: string = 'uc-search-consents-info';
const resetSearchFieldEventName: string = 'uc-reset-consents-info-search';
const saveAndCloseBtn: string = 'uc-save-settings-and-close-button';
const sidebarToggleId: string = 'uc-infomodal-sidebar';
const MIN_LENGTH_SEARCH_QUERY: number = 3;

export default class ConsentsInfoModalViewController {
    public consentsInfoModalId = consentsInfoModalId;
    private _scrollTop = 0;
    private searchQuery: any = '';
    private updateViewSet: any;
    private settingsService: SettingsService;
    private utilityService: UtilityService;
    private showNoResults: boolean = false;
    private hiddenSidebar: boolean = false;

    constructor(updateView, utilityService) {
        this.updateViewSet = updateView;
        this.settingsService = utilityService.settingsService;
        this.utilityService = utilityService;
    }

    private setScrollPosition() {
        const scrollDiv = document.getElementById(consentsInfoForSearchingContentId);
        this._scrollTop = scrollDiv.scrollTop;
    }

    private getScrollPosition() {
        const scrollDiv = document.getElementById(consentsInfoForSearchingContentId);
        scrollDiv.scrollTop = this._scrollTop;
    }

    public updateView() {
        this.setScrollPosition();
        const parent = document.getElementById(parentId);
        parent.innerHTML = this.htmlString;
        this.initHandling();
    }

    public initHandling() {
        if (this.visible) {
            const wrapper: HTMLElement = document.getElementById(consentsInfoModalId);
            this.utilityService.developmentLog('initializing info-modal view');
            this.getScrollPosition();

            _.forEach(DataService.consents, consent => {
                const toggleConsentElement: HTMLElement = wrapper.querySelector(`#toggle-consent-${consent.templateId}`);

                if (toggleConsentElement) {
                    toggleConsentElement.onclick = (event: any) => {
                        event.preventDefault();
                        event.stopPropagation();
                        this.updateSelectedStatusForConsent(consent);
                    };
                }

                const sideBarConsentItem: HTMLElement = wrapper.querySelector(`[data-scrollposition='${consent.templateId.toString()}']`);
                if (sideBarConsentItem) {
                    sideBarConsentItem.onclick = (event: any) => {
                        event.preventDefault();
                        event.stopPropagation();

                        const element = wrapper.querySelector(`[data-scrollanchor='${consent.templateId}']`);
                        if (element) {
                            element.scrollIntoView(true);
                        }
                    };
                }
            });

            _.forEach(DataService.categories, (category: Consent.Category) => {
                const sideBarCategoryItem: HTMLElement = wrapper.querySelector(`[data-scrollposition='${category.categorySlug.toString()}']`);

                if (sideBarCategoryItem) {
                    sideBarCategoryItem.onclick = (event: any) => {
                        event.preventDefault();
                        event.stopPropagation();

                        const element = wrapper.querySelector(`[data-scrollanchor='${category.categorySlug}']`);
                        if (element) {
                            element.scrollIntoView(true);
                        }
                    };
                }
            });

            const sideBarToggleBtn: HTMLElement = wrapper.querySelector(`[data-toggle='${sidebarToggleId}']`);
            if (sideBarToggleBtn) {
                sideBarToggleBtn.onclick = (event: any) => {
                    event.preventDefault();
                    event.stopPropagation();
                    this.toggleSidebar();
                };
            }

            const buttons = wrapper.getElementsByClassName(saveAndCloseBtn);
            _.forEach(buttons, saveInfoBtn => {
                if (saveInfoBtn) {
                    saveInfoBtn.onclick = () => {
                        const changedConsents = ConsentsStatusService.getConsentsThatHaveChanged(DataService.consents);
                        if (changedConsents && changedConsents.length) {
                            this.saveConsents();
                        }
                        this.closeModal();
                    };
                }
            });

            const closeModalBtn: HTMLElement = wrapper.querySelector(`[data-event='${closeConsentInfoModalEventName}']`);
            if (closeModalBtn) {
                closeModalBtn.onclick = () => { this.closeModal(); };
            }

            const resetSearchFieldBtn: HTMLElement = wrapper.querySelector(`[data-event='${resetSearchFieldEventName}']`);
            if (resetSearchFieldBtn) {
                resetSearchFieldBtn.onclick = () => { this.resetSearchField(); };
            }

            const searchField: any = wrapper.querySelector(`#${searchFieldId}`);
            if (searchField) {
                if (this.searchQuery) {
                    searchField.value = '';
                    searchField.focus();
                    searchField.value = this.searchQuery;
                }

                searchField.onkeyup = debounce((event: any) => {
                    this.utilityService.developmentLog('debounced search ', event.target.value);
                    this.searchQuery = event.target.value;

                    if (this.searchQuery.length >= MIN_LENGTH_SEARCH_QUERY) {
                        const contentElementForSearching = document.getElementById(consentsInfoForSearchingContentId);
                        this.search(contentElementForSearching);
                    }
                }, 500);
            }
        }
    }

    private toggleSidebar() {
        this.hiddenSidebar = !this.hiddenSidebar;
        this.updateView();
    }

    private updateSelectedStatusForConsent(consent: Consent.Consent) {
        if (GetConsentsService.isConsentEssential(consent)) {
            return;
        }

        ConsentsStatusService.setUnsavedConsentStatus(consent, !consent.unsavedConsentStatus);
        this.updateView();
    }

    private resetCategoriesForView() {
        this.showNoResults = false;
    }

    private saveConsents() {
        const action = 'onToggleConsent';
        const updatedBy = 'explicit';
        this.utilityService.saveConsentsService.saveConsents(action, updatedBy);
        this.settingsService.saveFirstUserInteraction();
        this.settingsService.bannerIsVisible = false;
        this.updateViewSet();
    }

    private closeModal() {
        this.searchQuery = '';
        this.visible = false;

        const dataExchangeService = new DataExchangeService();
        dataExchangeService.dataExchangeArray = this.settingsService.settings.dataExchangeOnPage;
        dataExchangeService.dispatchEvent('uc_privacyInformationCenterClosed', { event: 'uc_privacyInformationCenterClosed' });
        this.updateViewSet();
    }

    private resetSearchField() {
        this.searchQuery = '';
        this.resetCategoriesForView();
        this.updateView();
    }

    private search(contentElementForSearching) {
        const goalCategories = [];
        const query: string = this.searchQuery.toLowerCase();

        const categoryContentElements = contentElementForSearching.querySelectorAll(`.${categoryInfoContentClassName}`);

        _.forEach(categoryContentElements, categoryContentElement => {
            const categoryInnerText = categoryContentElement.innerText.toLowerCase();

            if (~categoryInnerText.indexOf(query)) {
                const goalConsentsInCategory = [];
                const categoryConsentElements = contentElementForSearching.querySelectorAll(`.${consentInfoContentClassName}`);

                _.forEach(categoryConsentElements, categoryConsentContentElement => {
                    const consentInnerText = categoryConsentContentElement.innerText.toLowerCase();

                    if (~consentInnerText.indexOf(query)) {
                        const consentId = categoryConsentContentElement.dataset.id;
                        const consent = _.find(DataService.consents, { templateId: consentId });
                        if (consent) {
                            goalConsentsInCategory.push(consent);
                        }
                    }
                });

                const categoryId: number = categoryContentElement.querySelector('.uc-info-modal-category-title').dataset.id;
                let category: Consent.Category = _.find(DataService.categories, { categorySlug: categoryId });

                if (category) {
                    if (goalConsentsInCategory.length) {
                        category = _.omit(category, ['consents']);
                        category.consents = _.cloneDeep(goalConsentsInCategory);
                    }
                    goalCategories.push(category);
                }
            }
        });

        if (goalCategories.length) {
            const wrapper = document.getElementById(consentsInfoModalId);
            const element = this.getElement(`[id='${goalCategories[0].consents[0].templateId}']`, wrapper) || this.getElement(`[data-id='${goalCategories[0].consents[0].templateId}']`, wrapper);

            if (element) {
                element.scrollIntoView(true);
                this.showNoResults = false;
                this.updateView();
            }
        } else {
            this.showNoResults = (query.length > MIN_LENGTH_SEARCH_QUERY);
            this.updateView();
        }
    }

    private getElement(selector: string, wrapper: HTMLElement): Element {
        return wrapper.querySelector(selector);
    }

    public getHtml() {
        this.utilityService.developmentLog('getting info-modal view');
        let viewHTML: any;
        const layoutParams: any = this.getPreparedLayoutParams();

        switch (layoutParams.view) {
            case 'consent-info-modal-template-1':
            case 'consent-info-modal-template-8':
            case 'consent-info-modal-template-11':
            case 'consent-info-modal-template-5':
            case 'consent-info-modal-template-12':
                viewHTML = consentInfoModalTemplate;
                break;

            default:
                break;
        }

        this.utilityService.developmentLog('got info-modal view');

        const viewData = {
            settings: this.settingsService.settings || {},
            categories: DataService.categories,
            searchQuery: this.searchQuery,
            showNoResults: this.showNoResults,
            hiddenSidebar: this.hiddenSidebar,
            elementsVisibility: this.settingsService.elementsVisibility,
            consentTemplates: DataService.consentTemplates,
            vendors: this.utilityService.cmpIABService.vendors,
            purposes: this.utilityService.cmpIABService.purposes,
            features: this.utilityService.cmpIABService.features
        };
        return viewHTML(viewData);
    }

    private getPreparedLayoutParams() {
        const layouts = SettingsService.layouts;
        const goalLayout = _.find(layouts, {
            name: this.settingsService.settings.layout,
        });
        return {
            view: goalLayout.consentInfoModalView,
        };
    }

    public set visible(_visible) {
        this.settingsService.infoModelIsVisible = _visible;
    }

    public get visible() {
        return this.settingsService.elementsVisibility.infoModel;
    }

    public get htmlString(): string {
        if (this.visible) {
            return this.getHtml();
        }
        return '';
    }
}
